import { Block, Flex, ProductColor, Typography, colors } from '@aloft/toolkit-sui';

import { useMemo } from 'react';
import { CartesianGrid, ComposedChart, ReferenceArea, Scatter } from 'recharts';

import { GraphTooltipWrapper } from '../../../graphs/components/GraphTooltipWrapper.tsx';
import { GraphXAxis } from '../../../graphs/components/GraphXAxis.tsx';
import { GraphYAxis } from '../../../graphs/components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../../../graphs/components/ResponsiveGraphWrapper.tsx';
import {
  QualityTierChartData,
  QualityTierChartPoint,
} from './getQualityTierChartData.ts';

const CONDITION_TICKS = [1, 2, 3, 4, 5, 6];
const CHART_HEIGHT = 280;

const tierColor = (tier: number): string =>
  colors[`qualityTier${tier}` as keyof typeof colors] as string;

type ScatterDotProps = {
  cx?: number;
  cy?: number;
  payload?: QualityTierChartPoint;
};

const ScatterDot = (props: ScatterDotProps) => {
  const { cx, cy, payload } = props;
  if (cx === undefined || cy === undefined || !payload) return null;
  const fill = tierColor(payload.qualityTier);
  if (payload.isSelected) {
    return <circle cx={cx} cy={cy} r={5} fill={colors.graphBackground} stroke={fill} strokeWidth={2} />;
  }
  return <circle cx={cx} cy={cy} r={4} fill={fill} />;
};

const ChartLegend = ({ tiers }: { tiers: number[] }) => (
  <Flex alignItems="center" gap="12px" flexShrink={0}>
    {tiers.map((tier) => (
      <Flex key={tier} alignItems="center" gap="4px">
        <Block
          width="8px"
          height="8px"
          borderRadius="2px"
          backgroundColor={`qualityTier${tier}` as ProductColor}
        />
        <Typography variant="body2" color="textSecondary">
          Tier {tier}
        </Typography>
      </Flex>
    ))}
    <Flex alignItems="center" gap="4px">
      <Block
        width="8px"
        height="8px"
        borderRadius="2px"
        backgroundColor="graphAreaBackgroundOpaque"
      />
      <Typography variant="body2" color="textSecondary">
        25th-75th Percentile
      </Typography>
    </Flex>
  </Flex>
);

export const QualityTierChart = ({ chartData }: { chartData: QualityTierChartData }) => {
  const { points, tiers, percentile25, percentile75 } = chartData;

  const pointsByTier = useMemo(() => {
    const grouped = new Map<number, QualityTierChartPoint[]>();
    for (const point of points) {
      const existing = grouped.get(point.qualityTier) ?? [];
      existing.push(point);
      grouped.set(point.qualityTier, existing);
    }
    return grouped;
  }, [points]);

  const xDomain = useMemo(() => {
    const prices = points.map((point) => point.salePrice);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.05 || 10000;
    return [min - padding, max + padding] as [number, number];
  }, [points]);

  if (points.length < 2) return null;

  return (
    <Block
      border="1px solid"
      borderColor="strokeDefault"
      padding="16px"
      width="100%"
    >
      <Flex justifyContent="space-between" alignItems="center" marginBottom="12px" gap="8px">
        <Typography variant="h4" color="textSecondary">
          Sale Price vs. Condition Rating
        </Typography>
        <ChartLegend tiers={tiers} />
      </Flex>
      <ResponsiveGraphWrapper disableAnimations={false} height={CHART_HEIGHT}>
        <ComposedChart margin={{ top: 8, left: 8, bottom: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.strokeLight} />
          <GraphXAxis
            type="number"
            dataKey="salePrice"
            name="Sale Price"
            domain={xDomain}
            tickFormatter={(value: number) => `$${Math.round(value / 1000)}k`}
          />
          <GraphYAxis
            type="number"
            dataKey="condition"
            name="Condition"
            label="Condition"
            domain={[6.5, 0.5]}
            ticks={CONDITION_TICKS}
            tickFormatter={(value: number) => `C${value}`}
            allowDataOverflow
          />
          {percentile25 !== undefined && percentile75 !== undefined && (
            <ReferenceArea
              x1={percentile25}
              x2={percentile75}
              fill={colors.graphAreaBackground}
              ifOverflow="extendDomain"
            />
          )}
          <GraphTooltipWrapper disableAnimations={false}>
            {({ payload }) => {
              const point = (payload[0] as { payload: QualityTierChartPoint } | undefined)?.payload;
              if (!point) return null;
              return (
                <>
                  <Typography variant="h4">{point.label}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Sale Price: {point.salePriceLabel}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Condition: {point.conditionLabel}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Quality Tier: Tier {point.qualityTier}
                  </Typography>
                </>
              );
            }}
          </GraphTooltipWrapper>
          {tiers.map((tier) => (
            <Scatter
              key={tier}
              name={`Tier ${tier}`}
              data={pointsByTier.get(tier) ?? []}
              dataKey="condition"
              fill={tierColor(tier)}
              shape={ScatterDot as never}
              isAnimationActive={false}
              legendType="none"
            />
          ))}
        </ComposedChart>
      </ResponsiveGraphWrapper>
    </Block>
  );
};
